import React, {Component} from "react";
import {Location, Visit} from "../types/Types";
import {Col, Row} from "react-bootstrap";
import "../../App.css";
import VisitElement from "./VisitElement";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHammer, faPlane, faHotel, faUmbrellaBeach, faGraduationCap, faTimes, faPencilAlt, faStar, faTags, faTrain} from '@fortawesome/free-solid-svg-icons'
import {Link} from "react-router-dom";
import VerificationTag from "../tag/VerificationTag";
import {getCountryFlag} from "../util/Util";

interface Props {
    sortType: string;
    location: Location | undefined;
    toggleVisited: (locationId: string) => void;
    position: number;
    admin: boolean;
    visit: Visit | undefined;
    validLocationTagNames: Set<string>;
}

// Helper function to check if location is open
const isLocationOpen = (location: Location): boolean => {
    if (!location.openingDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const openingDate = new Date(location.openingDate);
    openingDate.setHours(0, 0, 0, 0);
    return openingDate <= today;
};

const getValidDate = (dateString: string | undefined): Date | undefined => {
    if (!dateString) return undefined;

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return undefined;

    return date;
};

const getPubAge = (location: Location): string | undefined => {
    const openingDate = getValidDate(location.openingDate);
    if (!openingDate) return "1d";
    if (location.pubIsClosed && !getValidDate(location.closingDate)) return undefined;

    const endDate = getValidDate(location.closingDate) || new Date();
    if (endDate < openingDate) return "1d";

    const dayCount = Math.max(
        Math.floor((endDate.getTime() - openingDate.getTime()) / (1000 * 60 * 60 * 24)),
        1
    );

    let monthCount = (endDate.getFullYear() - openingDate.getFullYear()) * 12;
    monthCount += endDate.getMonth() - openingDate.getMonth();

    if (endDate.getDate() < openingDate.getDate()) {
        monthCount--;
    }

    if (monthCount < 1) return `${dayCount}d`;

    const years = Math.floor(monthCount / 12);
    const months = monthCount % 12;

    if (years > 0 && months > 0) return `${years}y ${months}m`;
    if (years > 0) return `${years}y`;
    return `${months}m`;
};

function LocationRow(props: Props) {
    const pubAge = props.location ? getPubAge(props.location) : undefined;
    const tagCount = new Set(
        props.location?.tags
            ?.map((tagName) => tagName.trim().toLowerCase())
            .filter((tagName) => props.validLocationTagNames.has(tagName))
    ).size;

    return (
        <>
            {props.location && <Row className="align-middle flex-nowrap pub-row">
                <Col className="d-flex flex-column align-items-start justify-content-center text-truncate">
                    <div>
                        <Link to={`/location/${props.location.id}/comments`} className={"p-0"}>
                            <div className={"location-tag"}>{props.location.name}</div>
                        </Link>
                        {props.visit && props.visit.verified && <div className={"d-inline-block ml-1"}><VerificationTag active={false}/></div>}
                        {props.location.airport && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-plane" icon={faPlane} />
                        </small>}
                        {props.location.hotel && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-hotel" icon={faHotel} />
                        </small>}
                        {props.location.holidayPark && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-holidaypark" icon={faUmbrellaBeach} />
                        </small>}
                        {props.location.isUniversity && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-university" icon={faGraduationCap} />
                        </small>}
                        {props.location.trainStation && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-train" icon={faTrain} />
                        </small>}
                        {props.location.pubIsClosed && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-closed" icon={faTimes} />
                        </small>}
                        {(props.location.pubIsTemporaryClosed && !props.location.pubIsClosed) && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-temporary" icon={faHammer} />
                        </small>}
                        {(props.location.bonus) && <small className="text-truncate">
                            <FontAwesomeIcon className="ml-1 animated zoomIn icon-bonus" icon={faStar} />
                        </small>}
                    </div>
                    {props.location && (
                        <div className="ml-1">
                            <small className="text-truncate">
                                <div className="d-inline-block" style={{ marginRight: "0.3em" }}>{getCountryFlag(props.location.country || '')}</div>
                                {pubAge && pubAge != '1d' && <div className="d-inline-block pub-age-tag" title="Pub age">{pubAge}</div>}
                                {tagCount > 0 && (
                                    <div className="d-inline-block pub-age-tag" title="Pub tags">
                                        {tagCount} <FontAwesomeIcon icon={faTags} />
                                    </div>
                                )}
                                <div className="d-inline-block">{props.location.address}</div>
                            </small>
                        </div>
                    )}
                </Col>
                <Col className="col-auto d-flex">
                     <VisitElement
                         displaySessions={props.sortType === 'sessions'}
                         displayLikes={props.sortType === 'likes'}
                         sessionCount={props.location.sessionUniqueCount}
                         likesCount={props.location.likeCount}
                         visitCount={props.location.visitCount}
                         visited={props.location.visited}
                         id={props.location.id}
                         isOpen={isLocationOpen(props.location)}
                         toggleVisited={props.toggleVisited} />
                </Col>
            </Row>}
        </>
    )
}

export default LocationRow;
